import { object, string } from 'yup'
import React, { useState } from 'react'
import TextButton from '@/shared/components/buttons/TextButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/pro-solid-svg-icons'
import { DialogModal } from '@/shared/components/DialogModal'
import { Form, Formik } from 'formik'
import FormikTextInput from '@/shared/forms/components/FormikTextInput'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'

type FreeTextMedicineDisplayProps = {
  medicine: string
  onChange: (value: string) => void
  variant?: 'default' | 'review'
  editDialogOpen?: boolean
  onEditDialogOpenChange?: (open: boolean) => void
}

export default function FreeTextMedicineDisplay({
  medicine,
  onChange,
  variant = 'default',
  editDialogOpen,
  onEditDialogOpenChange,
}: FreeTextMedicineDisplayProps) {
  const [internalEditDialogOpen, setInternalEditDialogOpen] = useState(false)
  const showMedicineEditDialog = editDialogOpen ?? internalEditDialogOpen

  function setShowMedicineEditDialog(open: boolean) {
    if (editDialogOpen === undefined) setInternalEditDialogOpen(open)
    onEditDialogOpenChange?.(open)
  }

  const editDialog = (
    <DialogModal
      show={showMedicineEditDialog}
      onClose={() => setShowMedicineEditDialog(false)}
      motionPreset="editorial"
    >
      <MedicineEditDialog
        initialValue={medicine}
        onFinishEdit={(value) => {
          onChange(value)
          setShowMedicineEditDialog(false)
        }}
        onClose={() => setShowMedicineEditDialog(false)}
      />
    </DialogModal>
  )

  if (variant === 'review') {
    return (
      <>
        <p className="whitespace-pre-wrap text-[14.5px] font-medium leading-[21px] text-[#0B0B0F]">
          {medicine}
        </p>
        {editDialog}
      </>
    )
  }

  return (
    <div className="mb-2 flex flex-col gap-y-1.5">
      <p className="h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-gray-100 px-2.5 py-1.5 text-gray-600 ring-1 ring-inset ring-gray-300">
        {medicine}
      </p>
      <TextButton
        title={
          <span className="block w-full py-1">
            <FontAwesomeIcon icon={faPencil} className="mr-2" /> Edit medicine
            description
          </span>
        }
        color="blue"
        onClick={() => setShowMedicineEditDialog(true)}
      />
      {editDialog}
    </div>
  )
}

type MedicineEditDialogProps = {
  initialValue: string
  onFinishEdit: (value: string) => void
  onClose: () => void
}

function MedicineEditDialog({
  initialValue,
  onFinishEdit,
  onClose,
}: MedicineEditDialogProps) {
  return (
    <Formik
      initialValues={{
        content: initialValue,
      }}
      onSubmit={({ content }, { setSubmitting }) => {
        onFinishEdit(content)
        setSubmitting(false)
      }}
      validationSchema={object({
        content: string().required(
          'The doctor needs the medicine information to access your request accurately',
        ),
      })}
      enableReinitialize={true}
    >
      {({ isSubmitting }) => (
        <Form>
          <h2 className="font-display text-lg text-gray-800">
            Medicine Description
          </h2>
          <p className="mb-2 font-sans text-sm text-gray-700">
            Describe the medicine you are requesting. You may include the name,
            strength, and quantity.
          </p>

          <FormikTextInput
            label="Describe the medicine you are requesting"
            name="content"
            size="full"
            variant="multiline"
            autocomplete=""
            hideLabel={true}
          />

          <div className="mt-2.5 flex flex-col-reverse items-center justify-stretch gap-x-3.5 gap-y-2.5 sm:flex-row">
            <TextButton
              title="Cancel"
              color="gray"
              appearance="ring"
              type="button"
              className="w-full"
              disabled={isSubmitting}
              onClick={() => onClose()}
            />
            <TextButton
              title={
                <>
                  {isSubmitting ? (
                    <span>Loading</span>
                  ) : (
                    <>
                      <span className="relative">Confirm</span>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2"
                      />
                    </>
                  )}
                </>
              }
              color="blue"
              type="submit"
              className="w-full"
              loading={isSubmitting}
            />
          </div>
        </Form>
      )}
    </Formik>
  )
}
